/**
 * App frame. With the `lanesTab` flag (default on): the tab shell (components/shell/TabShell.tsx)
 * — the tab strip is the navigation (Home ⌥0, project tabs ⌥1–9, Settings), an inspector slot and
 * the status line; `g 0` Home, `g i` / `g n` / `g o` Home's views, `g ,` Settings. The escape
 * hatch `?flags=-lanesTab` keeps the old frame for one release: mobile content + tab bar
 * (Now · Inbox · (+) · Projects · You), desktop left rail + list/detail panes.
 * Both: ⌘K palette, `c` capture and `n` new session (⌘N is reserved by browsers), which take the
 * current tab's project.
 */

import { Link, Outlet, useMatches, useNavigate, useRouterState } from '@tanstack/react-router'
import { shortcutLabel, shortcutProps, useShortcut } from '@viko/transcript-ui'
import {
  AudioLines, // g8-voice
  BookMarked,
  CloudOff,
  Folder,
  Inbox,
  LayoutGrid,
  Package,
  Plus,
  Radio,
  RefreshCw,
  UserRound,
} from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { CallProvider } from '../lib/call/CallProvider.js' // viko-call
import { isEnabled } from '../lib/flags.js' // lanes-shell
import { homeHref, projectHref, projectOfPath } from '../lib/ia.js' // ia-shell
import { useLatencyRecorder } from '../lib/latency-store.js' // g12-overview
import { needsYouItemIds } from '../lib/needs-you.js'
import { isLive, useConnection, useHubSync, useMachines, useRuns } from '../lib/queries.js'
import { useRoutingHintSync } from '../lib/routing-prefs.js'
import { focusPrimaryInputOnSlash } from '../lib/shortcut.js'
import { onServiceWorkerMessage } from '../lib/sw-register.js'
import { useAppBadge } from '../lib/use-app-badge.js' // g12-overview
import { useNotificationActions } from '../lib/use-notification-actions.js' // g12-overview
import { useWorkSync } from '../lib/work-sync.js'
import { AppLock, IdleLockPrompt } from './AppLock.js'
import { ApprovalTray } from './ApprovalTray.js'
import { type AppUi, AppUiContext, type CapturePrefill, type StartPrefill } from './app-ui.js'
import { Wordmark } from './brand.js'
import { CaptureSheet, signBrainJob } from './Capture.js'
import { CheatSheet } from './CheatSheet.js'
import { CommandPalette } from './CommandPalette.js'
import { useNeedsYou } from './inbox/useNeedsYou.js'
import { ContentLockWatcher } from './lock/ContentLock.js'
import { RowActionsProvider } from './RowActions.js'
import { StartSession } from './StartSession.js'
import { TabShell } from './shell/TabShell.js' // ia-shell
import { useTabShortcuts } from './tabs/useTabShortcuts.js' // lanes-shell

export function AppShell() {
  useHubSync()
  useBrainJobs()
  const nav = useNavigate()
  const matches = useMatches()
  const runs = useRuns()
  useWorkSync(runs.data)
  const machines = useMachines()
  const conn = useConnection()
  const [capture, setCapture] = useState<{ open: boolean; prefill?: CapturePrefill }>({
    open: false,
  })
  const [start, setStart] = useState<{ open: boolean; prefill?: StartPrefill }>({ open: false })
  const [palette, setPalette] = useState(false)
  const [cheat, setCheat] = useState(false) // keyboard-shortcuts: ? cheat sheet
  const inSession = matches.some((m) => (m.params as { runId?: string }).runId)
  // ---- ia-shell: the tab shell (default) or the old rail frame (`?flags=-lanesTab`) ----
  const tabbed = useMemo(() => isEnabled('lanesTab'), [])
  // ⌥/⌘0–9 and g 0–9 switch tabs (0 = Home).
  useTabShortcuts(tabbed)
  // Composers take the current tab's project: `c`, `n`, the phone's (+) and every openCapture /
  // openStart without an explicit project (navigation-ia.md §2.1 P0 context loss).
  const routeProject = useRouterState({
    select: (s) =>
      projectOfPath(s.location.pathname) ??
      (s.location.pathname === '/home'
        ? ((s.location.search as { project?: string }).project ?? undefined)
        : undefined),
  })
  const here = useRef(routeProject)
  here.current = routeProject
  // ---- end ia-shell ----

  useRoutingHintSync() // g11-routing

  const ui = useMemo<AppUi>(
    () => ({
      openCapture: (prefill) => {
        const pid = here.current
        const p: CapturePrefill | undefined =
          pid && !prefill?.targetId ? { ...prefill, targetId: `project:${pid}` } : prefill
        setCapture({ open: true, ...(p ? { prefill: p } : {}) })
      },
      openStart: (prefill) => {
        const pid = here.current
        const p: StartPrefill | undefined =
          pid && !prefill?.projectId ? { ...prefill, projectId: pid } : prefill
        setStart({ open: true, ...(p ? { prefill: p } : {}) })
      },
      openPalette: () => setPalette(true),
      openShortcuts: () => setCheat(true),
    }),
    [],
  )

  // ---- keyboard-shortcuts: registry-driven global keys (typing-guarded, see @viko/transcript-ui keys) ----
  // Capture is plain "c" and New session plain "n" (⌘N/⌘T are browser-owned); ⌘K works everywhere.
  useShortcut('global.palette', () => setPalette((p) => !p))
  useShortcut('global.capture', () => ui.openCapture())
  useShortcut('global.newSession', () => ui.openStart())
  useShortcut('global.help', () => setCheat((c) => !c))
  // ---- end keyboard-shortcuts ----

  // Keyboard: / (focus input), g-jumps (ignored while typing).
  const g = useRef(0)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement)?.closest?.(
        'input, textarea, select, [contenteditable="true"]',
      )
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return
      if (focusPrimaryInputOnSlash(e)) return
      if (e.key === 'g') {
        g.current = Date.now()
        return
      }
      if (Date.now() - g.current < 1200) {
        const pid = here.current
        const to = tabbed
          ? {
              i: homeHref({ v: 'needs' }),
              n: homeHref({ v: 'running' }),
              o: homeHref({ v: 'projects' }),
              p: homeHref({ v: 'projects' }),
              ',': '/settings',
              y: '/settings',
              // In a project tab, `g h` is its History (the view keys live in ProjectChrome).
              ...(projectOfPath(location.pathname) ? {} : { h: '/home' }),
              s: pid ? projectHref(pid, 'settings') : '/settings',
            }[e.key]
          : { i: '/inbox', p: projectsHome(), n: '/now', y: '/you', o: '/overview' }[e.key]
        g.current = 0
        if (to) void nav({ href: to })
      }
    }
    addEventListener('keydown', onKey)
    return () => removeEventListener('keydown', onKey)
  }, [nav, tabbed])

  // Notification clicks from the service worker.
  useEffect(
    () =>
      onServiceWorkerMessage((m) => {
        // now-inbox: push deep links carry a query (`/inbox?i=decision:<id>`).
        if (m.type === 'navigate' && m.url) void nav({ href: m.url })
      }),
    [nav],
  )

  // ---- g12-overview: app badge, notification buttons, blocked→known measurement ----
  const needs = useNeedsYou()
  useAppBadge(needs.ready ? needsYouItemIds(needs.rows) : undefined)
  useNotificationActions()
  useLatencyRecorder()
  // ---- end g12-overview ----
  const badge = needs.total // one count source: lib/needs-you.ts
  const liveCount = (runs.data ?? []).filter(isLive).length

  const sheets = (
    <>
      <CaptureSheet
        open={capture.open}
        prefill={capture.prefill}
        onClose={() => setCapture({ open: false })}
      />
      <StartSession
        open={start.open}
        prefill={start.prefill}
        onClose={() => setStart({ open: false })}
      />
      <CommandPalette open={palette} onClose={() => setPalette(false)} />
      <CheatSheet open={cheat} onClose={() => setCheat(false)} />
      <ApprovalTray />
      <AppLock />
      <ContentLockWatcher />
    </>
  )
  // ---- ia-shell ----
  if (tabbed)
    return (
      <AppUiContext.Provider value={ui}>
        {/* viko-call: one call app-wide (top-bar button, docked panel, call.show, ⌘⇧V) */}
        <CallProvider>
          <TabShell
            banners={
              <>
                <ConnBanner state={conn} />
                <IdleLockPrompt />
              </>
            }
          >
            <RowActionsProvider>
              <Outlet />
            </RowActionsProvider>
          </TabShell>
        </CallProvider>
        {sheets}
      </AppUiContext.Provider>
    )
  // ---- end ia-shell ----
  return (
    <AppUiContext.Provider value={ui}>
      <div className={`app${inSession ? ' hide-tabs' : ''}`}>
        <nav className="rail" aria-label="Main">
          <div className="rail-brand">
            <Wordmark size={19} />
            <span className="flex-1" />
            <button
              type="button"
              className="vk-iconbtn !w-auto px-2"
              aria-label="Command palette"
              {...shortcutProps('global.palette', 'Command palette')}
              onClick={() => setPalette(true)}
            >
              <kbd className="text-xs">{shortcutLabel('global.palette')}</kbd>
            </button>
          </div>
          <button
            type="button"
            className="rail-item !text-text-1"
            {...shortcutProps('global.capture', 'Capture an idea')}
            onClick={() => ui.openCapture()}
          >
            <Plus size={16} aria-hidden="true" /> Capture{' '}
            <kbd className="rail-count">{shortcutLabel('global.capture')}</kbd>
          </button>
          <RailLink to="/now" icon={<Radio size={16} />} count={liveCount}>
            Now
          </RailLink>
          {/* ---- g12-overview ---- */}
          <RailLink to="/overview" icon={<LayoutGrid size={16} />}>
            Overview
          </RailLink>
          {/* ---- end g12-overview ---- */}
          <RailLink to="/inbox" icon={<Inbox size={16} />} count={badge} hot>
            Inbox
          </RailLink>
          <RailLink to={projectsHome()} icon={<Folder size={16} />}>
            Projects
          </RailLink>
          {/* ---- g8-voice ---- */}
          <RailLink to="/voice" icon={<AudioLines size={16} />}>
            Voice
          </RailLink>
          {/* ---- end g8-voice ---- */}
          <RailLink to="/releases" icon={<Package size={16} />}>
            Releases
          </RailLink>
          <RailLink to="/rules" icon={<BookMarked size={16} />}>
            Rules
          </RailLink>
          <RailLink to="/you" icon={<UserRound size={16} />}>
            You
          </RailLink>
          <div className="rail-label">Machines</div>
          {(machines.data ?? []).map((m) => (
            <Link
              key={m.id}
              to="/you/machines"
              className="!h-auto !py-1.5"
              activeOptions={{ exact: true }}
              activeProps={{}}
            >
              <span
                className={`pulse ${m.online ? 'text-status-ok' : 'text-status-idle'}`}
                aria-hidden="true"
              />
              <span className="grid">
                <span className="text-[14px]">{m.name}</span>
                <span className="text-[14px] text-text-3">
                  {m.online ? 'Online' : 'Offline'}
                  {m.caps ? ` · ${m.caps.running}/${m.caps.maxRuns} runs` : ''}
                </span>
              </span>
            </Link>
          ))}
          <span className="flex-1" />
          <button
            type="button"
            className="rail-item text-[14px] !text-text-3"
            {...shortcutProps('global.help', 'Keyboard shortcuts')}
            onClick={() => ui.openShortcuts?.()}
          >
            Keyboard shortcuts <kbd className="rail-count">{shortcutLabel('global.help')}</kbd>
          </button>
        </nav>
        <div className="flex min-h-0 min-w-0 flex-col">
          <ConnBanner state={conn} />
          <IdleLockPrompt />
          <RowActionsProvider>
            <Outlet />
          </RowActionsProvider>
        </div>
        <nav className="tabbar" aria-label="Main">
          <TabLink to="/now" icon={<Radio size={21} />} label="Now" />
          <TabLink to="/inbox" icon={<Inbox size={21} />} label="Inbox" badge={badge} />
          <button
            type="button"
            className="capture-btn"
            aria-label="Capture"
            {...shortcutProps('global.capture', 'Capture')}
            onClick={() => ui.openCapture()}
          >
            <Plus size={22} strokeWidth={2.4} />
          </button>
          <TabLink to={projectsHome()} icon={<Folder size={21} />} label="Projects" />
          <TabLink to="/you" icon={<UserRound size={21} />} label="You" />
        </nav>
      </div>
      {sheets}
    </AppUiContext.Provider>
  )
}

/**
 * Brain jobs the hub prepared but no device has signed yet (e.g. captured via the share target
 * or while this device was offline): sign them (device key, no tap) whenever we connect.
 */
function useBrainJobs() {
  const b = useBackend()
  const conn = useConnection()
  useEffect(() => {
    if (conn !== 'live') return
    let alive = true
    void b.api
      .brainJobs()
      .then(async (jobs) => {
        for (const j of jobs) if (alive) await signBrainJob(b, j).catch(() => {})
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [b, conn])
}

function RailLink({
  to,
  icon,
  children,
  count,
  hot,
}: {
  to: string
  icon: ReactNode
  children: ReactNode
  count?: number
  hot?: boolean
}) {
  return (
    <Link to={to} activeProps={{ 'aria-current': 'page' }}>
      <span aria-hidden="true">{icon}</span>
      {children}
      {count ? <span className={`rail-count${hot ? ' is-hot' : ''}`}>{count}</span> : null}
    </Link>
  )
}

function TabLink({
  to,
  icon,
  label,
  badge,
}: {
  to: string
  icon: ReactNode
  label: string
  badge?: number
}) {
  return (
    <Link
      to={to}
      activeProps={{ 'aria-current': 'page' }}
      aria-label={badge ? `${label}, ${badge} waiting` : label}
    >
      {icon}
      <span aria-hidden="true">{label}</span>
      {badge ? (
        <span className="tab-badge" aria-hidden="true">
          {badge}
        </span>
      ) : null}
    </Link>
  )
}

function ConnBanner({ state }: { state: string }) {
  // Only after a first connection attempt had time to settle; avoids a flash on load.
  const [show, setShow] = useState(false)
  useEffect(() => {
    if (state === 'live' || state === 'idle' || state === 'connecting') return setShow(false)
    const t = setTimeout(() => setShow(true), 1200)
    return () => clearTimeout(t)
  }, [state])
  if (!show) return null
  return (
    <div className="conn-banner" role="status">
      {state === 'offline' ? (
        <CloudOff size={14} aria-hidden="true" />
      ) : (
        <RefreshCw size={14} className="vk-spin" aria-hidden="true" />
      )}
      {state === 'offline' ? 'Offline — showing the last synced state.' : 'Reconnecting to Viko…'}
    </div>
  )
}

/** List/detail layout used by Now, Inbox and Projects. */
export function Split({
  list,
  detail,
  hasDetail,
  fallback,
  collapseWhenEmpty,
}: {
  list: ReactNode
  detail: ReactNode
  hasDetail: boolean
  fallback?: ReactNode
  /** Desktop: without a selection the list takes the full width (Inbox). */
  collapseWhenEmpty?: boolean
}) {
  const split = hasDetail || !collapseWhenEmpty
  return (
    <div className={`app-main flex-1${split ? ' is-split' : ''}${hasDetail ? ' has-detail' : ''}`}>
      <div className="app-list">{list}</div>
      <div className="app-detail">{hasDetail ? detail : fallback}</div>
    </div>
  )
}

/** Projects opens the Lanes project tabs when that surface is enabled, else the classic list. */
function projectsHome(): '/p' | '/projects' {
  return isEnabled('lanesTab') ? '/p' : '/projects'
}
